import { Injectable } from '@angular/core';
import { Hex } from 'viem';

/**
 * localStorage layer for client-owned team metadata: names and default slot.
 * Liveness and ordering live on-chain (`teamOrderPacked`); this service only
 * handles the metadata the chain doesn't store.
 */
@Injectable({ providedIn: 'root' })
export class TeamStorageService {
  private readonly PREFIX = 'munch.teams.';

  private namesKey(address: Hex): string {
    return `${this.PREFIX}${address.toLowerCase()}.names`;
  }

  private defaultKey(address: Hex): string {
    return `${this.PREFIX}${address.toLowerCase()}.default`;
  }

  readNames(address: Hex): Record<number, string> {
    const raw = localStorage.getItem(this.namesKey(address));
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  writeNames(address: Hex, names: Record<number, string>): void {
    localStorage.setItem(this.namesKey(address), JSON.stringify(names));
  }

  setName(address: Hex, slot: number, name: string): void {
    const names = this.readNames(address);
    names[slot] = name;
    this.writeNames(address, names);
  }

  removeName(address: Hex, slot: number): void {
    const names = this.readNames(address);
    if (!(slot in names)) return;
    delete names[slot];
    this.writeNames(address, names);
  }

  readDefault(address: Hex): number {
    const raw = localStorage.getItem(this.defaultKey(address));
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
  }

  writeDefault(address: Hex, slot: number): void {
    localStorage.setItem(this.defaultKey(address), String(slot));
  }
}
